import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import {
  removeSelfServiceArticle,
  saveSelfServiceArticle,
  saveSelfServiceCategory,
  selfServiceArticleQuery,
  type SelfServiceArticle,
  type SelfServiceArticleForm,
  type SelfServiceCategory,
} from '../api/easeedeskSelfService'
import { INHERIT_ACCESS, type AccessOption } from '../api/easeedeskSolutions'
import { Modal } from '../ui/Modal'
import { DocumentAccessPicker } from './DocumentAccessPicker'
import { SettingsCheck } from '../ui/SettingsParts'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'

/**
 * Cele doua ferestre de scris ale Self-Service - `self_service/form.php`
 * ("Write an article" / "Edit the article") si `categories.php` - cu
 * scriptul din `public/js/self_service.js`: `#ssSave`, `.ssRemove` (cu
 * intrebarea "Take it down?"), `.ssCatSave` pe fiecare rand si
 * `#ssCategoryForm` pentru una noua. Dupa scriere portalul reincarca pagina;
 * aici se reincarca interogarile listei si ale categoriilor.
 *
 * Corpul se scrie intr-o casuta de text, nu in CKEditor: v4 nu are editorul
 * lui, si un `<textarea>` cu HTML-ul brut scrie in aceeasi coloana aceleasi
 * litere. E singura deosebire fata de portal, si e spusa pe ecran.
 */

const EMPTY: SelfServiceArticleForm = { ID: 0, category_id: 0, lang: 'ro', title: '', summary: '', body: '', self_serve_minutes: '', published: false, access: INHERIT_ACCESS }

function useReload() {
  const client = useQueryClient()

  return () => void client.invalidateQueries({ queryKey: ['easeedesk', 'self-service'] })
}

/** `form.php`: `editing` e articolul de pe rand (se cere intreg, cu corpul), `null` = unul nou. */
export function ArticleModal({ open, editing, categories, roles = [], people = [], onClose }: {
  open: boolean
  editing: SelfServiceArticle | null
  categories: SelfServiceCategory[]
  /** Ce se poate bifa in "Visible to"; vin cu pagina, numai pentru cine scrie. */
  roles?: AccessOption[]
  people?: AccessOption[]
  onClose(): void
}) {
  const reload = useReload()
  const detail = useQuery({ ...selfServiceArticleQuery(editing?.ID ?? 0), enabled: open && editing !== null })
  const [form, setForm] = useState<SelfServiceArticleForm>(EMPTY)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    if (editing === null) {
      setForm(EMPTY)
      return
    }
    if (detail.data) {
      const a = detail.data
      setForm({
        ID: a.ID,
        category_id: a.category_id,
        lang: a.lang,
        title: a.title,
        summary: a.summary,
        body: a.body,
        self_serve_minutes: a.self_serve_minutes === null ? '' : String(a.self_serve_minutes),
        published: a.published === 1,
        access: a.access ?? INHERIT_ACCESS,
      })
    }
  }, [open, editing, detail.data])

  const set = <K extends keyof SelfServiceArticleForm>(key: K, value: SelfServiceArticleForm[K]) => setForm((f) => ({ ...f, [key]: value }))

  const save = (ev: FormEvent) => {
    ev.preventDefault()
    setBusy(true)
    saveSelfServiceArticle(form)
      .then((reply) => {
        toast(reply.message, 'Saved', 'success')
        reload()
        onClose()
      })
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Not saved', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  const loading = editing !== null && !detail.data

  return (
    <Modal id="ssArticleModal" open={open} onClose={onClose} title={editing ? 'Edit the article' : 'Write an article'} footer={
      <>
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
        <button type="submit" form="ssArticleForm" className="btn btn-theme" id="ssSave" disabled={busy || loading}>
          Save
        </button>
      </>
    }>
      {loading ? (
        <Spinner />
      ) : (
        <form id="ssArticleForm" className="form-horizontal" onSubmit={save}>
          <input type="hidden" name="ID" value={form.ID} readOnly />
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="ssTitle">
              Title
            </label>
            <div className="col-sm-9">
              <input id="ssTitle" type="text" className="form-control" name="title" maxLength={255} required value={form.title} onChange={(e) => set('title', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="ssSummary">
              Summary
            </label>
            <div className="col-sm-9">
              <input id="ssSummary" type="text" className="form-control" name="summary" maxLength={500} value={form.summary} onChange={(e) => set('summary', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="ssCategory">
              Category
            </label>
            <div className="col-sm-4">
              <select id="ssCategory" className="form-control" name="category_id" value={form.category_id} onChange={(e) => set('category_id', Number(e.target.value))}>
                <option value={0}>no category</option>
                {categories.map((c) => (
                  <option key={c.ID} value={c.ID}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <label className="col-sm-2 control-label" htmlFor="ssLang">
              Language
            </label>
            <div className="col-sm-3">
              {/* O coloana, nicio unealta de traducere: magazinul cere intai
                  limba aleasa si, daca nu gaseste, le arata pe toate. */}
              <select id="ssLang" className="form-control" name="lang" value={form.lang} onChange={(e) => set('lang', e.target.value)}>
                <option value="ro">RO</option>
                <option value="en">EN</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="ssMinutes">
              How long it takes
            </label>
            <div className="col-sm-3">
              <input id="ssMinutes" type="number" className="form-control" name="self_serve_minutes" min={1} max={480} value={form.self_serve_minutes} onChange={(e) => set('self_serve_minutes', e.target.value)} />
            </div>
            <div className="col-sm-6 adm-muted" style={{ paddingTop: 7 }}>
              {/* Ce vede omul, asa cum o sa vada: fraza compusa din numar. */}
              {Number(form.self_serve_minutes) > 0 ? `Estimated fix: ${Number(form.self_serve_minutes)} ${Number(form.self_serve_minutes) === 1 ? 'minute' : 'minutes'}` : 'minutes, as the reader is told - leave empty if nobody measured'}
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="ssBody">
              Steps
            </label>
            <div className="col-sm-9">
              <textarea id="ssBody" className="form-control ss-editor" name="body" rows={12} required value={form.body} onChange={(e) => set('body', e.target.value)}></textarea>
              <span className="adm-muted">HTML is kept as written; there is no rich editor here.</span>
            </div>
          </div>
          <div className="form-group">
            <div className="col-sm-9 col-sm-offset-3">
              <SettingsCheck name="published" value="1" checked={form.published} onChange={(on) => set('published', on)} label="Published - people can read it" />
            </div>
          </div>
          <DocumentAccessPicker idPrefix="ssAccess" horizontal roles={roles} people={people} value={form.access ?? INHERIT_ACCESS} onChange={(next) => set('access', next)} />
        </form>
      )}
    </Modal>
  )
}

/** `.ssRemove`: "Take it down?" - se stinge, nu se sterge. */
export function takeDownArticle(article: SelfServiceArticle, reload: () => void): void {
  if (!window.confirm(`Take it down?\n\n"${article.title}" stops being visible. It is not deleted - a link somebody already sent still opens.`)) return
  removeSelfServiceArticle(article.ID)
    .then((reply) => {
      toast(reply.message, 'Taken down', 'success')
      reload()
    })
    .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Not removed', 'Error', 'error'))
}

export function useTakeDown(): (article: SelfServiceArticle) => void {
  const reload = useReload()

  return (article) => takeDownArticle(article, reload)
}

/** `categories.php`: un rand editabil pe categorie, cu bifa de salvat, si formularul de una noua. */
export function CategoriesModal({ open, categories, onClose }: { open: boolean; categories: SelfServiceCategory[]; onClose(): void }) {
  const reload = useReload()
  const [fresh, setFresh] = useState({ name: '', sort_order: 0 })

  const write = (row: { ID: number; name: string; sort_order: number; status: boolean }) =>
    saveSelfServiceCategory(row)
      .then((reply) => {
        toast(reply.message, 'Saved', 'success')
        reload()
      })
      .catch((err: unknown) => {
        toast(err instanceof Error ? err.message : 'Not saved', 'Error', 'error')
      })

  return (
    <Modal id="ssCategoriesModal" open={open} onClose={onClose} title="Categories" footer={
      <button type="button" className="btn btn-default" onClick={onClose}>
        Close
      </button>
    }>
      <table className="table table-condensed adm-list">
        <thead>
          <tr>
            <th style={{ width: 92 }}>Order</th>
            <th>Name</th>
            <th style={{ width: 90 }}>State</th>
            <th className="text-right" style={{ width: 60 }}>
              &nbsp;
            </th>
          </tr>
        </thead>
        <tbody>
          {categories.map((c) => (
            <CategoryRow key={c.ID} category={c} onSave={write} />
          ))}
        </tbody>
      </table>
      {categories.length === 0 && (
        <div className="adm-empty-inline">
          <i className="fas fa-tags"></i> No categories yet. Articles work without one, but a list of thirty with no grouping does not.
        </div>
      )}
      <hr />
      <form
        id="ssCategoryForm"
        className="form-inline"
        onSubmit={(ev) => {
          ev.preventDefault()
          void write({ ID: 0, name: fresh.name, sort_order: fresh.sort_order, status: true }).then(() => setFresh({ name: '', sort_order: 0 }))
        }}
      >
        <input type="text" className="form-control input-sm" name="name" maxLength={120} placeholder="New category" style={{ width: 260 }} required value={fresh.name} onChange={(e) => setFresh({ ...fresh, name: e.target.value })} />{' '}
        <input type="number" className="form-control input-sm" name="sort_order" style={{ width: 80 }} title="Order" value={fresh.sort_order} onChange={(e) => setFresh({ ...fresh, sort_order: Number(e.target.value) })} />{' '}
        <button type="submit" className="btn btn-success btn-sm">
          <i className="fas fa-plus-circle"></i> Add
        </button>
      </form>
    </Modal>
  )
}

function CategoryRow({ category, onSave }: { category: SelfServiceCategory; onSave(row: { ID: number; name: string; sort_order: number; status: boolean }): Promise<void> }) {
  const [row, setRow] = useState({ name: category.name, sort_order: category.sort_order, status: category.status === 1 })

  return (
    <tr data-id={category.ID} className={category.status === 1 ? undefined : 'adm-muted'}>
      <td>
        <input type="number" className="form-control input-sm ssCatOrder" value={row.sort_order} onChange={(e) => setRow({ ...row, sort_order: Number(e.target.value) })} />
      </td>
      <td>
        <input type="text" className="form-control input-sm ssCatName" maxLength={120} value={row.name} onChange={(e) => setRow({ ...row, name: e.target.value })} />
      </td>
      <td>
        <SettingsCheck className="ssCatStatus" value="1" checked={row.status} onChange={(on) => setRow({ ...row, status: on })} label="on" />
      </td>
      <td className="text-right adm-nowrap">
        <button type="button" className="btn btn-xs btn-default ssCatSave" title="Save" onClick={() => void onSave({ ID: category.ID, ...row })}>
          <i className="fas fa-check"></i>
        </button>
      </td>
    </tr>
  )
}
